import type { ComponentPropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';

type BaseButtonStyleProps = {
  backgroundColor?: string;
  disabled?: boolean;
  fontColor?: string;
  hoverBackgroundColor?: string;
};

// Style-only props that are part of the button family's public API; consumed by the CSS below, never forwarded to the DOM.
const STYLE_ONLY_PROPS = new Set(['backgroundColor', 'fontColor', 'hoverBackgroundColor']);
const shouldForwardProp = (prop: string) => !STYLE_ONLY_PROPS.has(prop);

type ButtonElementProps = ComponentPropsWithoutRef<'button'> & BaseButtonStyleProps;
type LinkElementProps = ComponentPropsWithoutRef<'a'> & BaseButtonStyleProps;

type ButtonTheme = {
  borderRadius?: (value: string | number) => string;
  fontSize?: (value: string | number) => string;
  fonts?: { family?: string };
  globalRadius?: string | number;
  standardRadius?: string | number;
};

const ButtonDefaultStyle = css<BaseButtonStyleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  &:focus-visible {
    outline-offset: 2px;
  }
  border: none;
  user-select: none;
  text-decoration: none;
  ${({ theme }) => (theme as ButtonTheme).borderRadius?.((theme as ButtonTheme).globalRadius ?? 0) ?? ''};
  font-family: ${({ theme }) => (theme as ButtonTheme).fonts?.family};
  color: ${({ fontColor }) => fontColor};
  background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};
  ${({ theme }) => (theme as ButtonTheme).fontSize?.('14px') ?? ''};
  -webkit-appearance: none;

  &:hover {
    background-color: ${({ hoverBackgroundColor }) => hoverBackgroundColor};
  }
`;

export const Button = styled.button.withConfig({ shouldForwardProp })<ButtonElementProps>`
  ${ButtonDefaultStyle}
`;

export const StandardButton = styled(Button)`
  padding: 16px 20px;
  margin: 0 10px;
  font-weight: 500;
  transition: all 0.1s ease-in;
  background-color: var(--color-primaryButton);
  color: ${({ disabled }) => (disabled ? 'var(--color-primaryButtonText-40)' : 'var(--color-primaryButtonText)')};
  ${({ theme }) => (theme as ButtonTheme).fontSize?.('16px') ?? ''};
  ${({ theme }) => (theme as ButtonTheme).borderRadius?.(8) ?? ''};

  ${Icon} {
    fill: ${({ disabled }) => (disabled ? 'var(--color-primaryButtonText-40)' : 'var(--color-primaryButtonText)')};
  }

  &:hover {
    background-color: ${({ disabled }) => (!disabled ? 'var(--color-primaryButtonHover)' : undefined)};
  }
`;

export const FlatButton = styled(StandardButton)`
  text-align: center;
  ${({ theme }) => (theme as ButtonTheme).fontSize?.('16px') ?? ''};
  line-height: 150%;
  font-weight: 500;
  padding: 8px;
  color: ${({ disabled }) => (disabled ? 'var(--color-secondaryButtonText-50)' : 'var(--color-secondaryButtonText)')};

  &:not([disabled]) {
    cursor: pointer;
  }

  &:hover,
  &:active {
    color: ${({ disabled }) => (disabled ? 'var(--color-secondaryButtonText-50)' : 'var(--color-secondaryButtonText)')};

    ${Icon} {
      fill: ${({ disabled }) =>
        disabled ? 'var(--color-secondaryButtonText-50)' : 'var(--color-secondaryButtonText)'};
    }
  }

  ${Icon} {
    fill: ${({ disabled }) => (disabled ? 'var(--color-secondaryButtonText-50)' : 'var(--color-secondaryButtonText)')};
  }
`;

export const CtaButton = styled(Button)`
  border-radius: ${({ theme }) => (theme as ButtonTheme).standardRadius};
  flex: 1 0 auto;
  text-align: center;
  padding: 14px 5px;
  font-weight: 500;
`;

export const LinkButton = styled.a.withConfig({ shouldForwardProp })<LinkElementProps>`
  ${ButtonDefaultStyle}
  ${({ theme }) => (theme as ButtonTheme).borderRadius?.(2) ?? ''};
  flex: 1 0 auto;
  padding: 10px 5px;
  font-weight: 500;
`;
